import { Card, Cluster, Container, Section, Stack } from "@humaniti/ui";
import type { CSSProperties } from "react";
import { SEO } from "../lib/seo";

const SECTION_LABEL: CSSProperties = {
  fontFamily: "var(--font-mono)",
  fontSize: "var(--fs-label)",
  letterSpacing: "0.08em",
  textTransform: "uppercase",
  color: "var(--fg-secondary)",
};

const H1: CSSProperties = {
  margin: 0,
  fontFamily: "var(--font-sans)",
  fontWeight: 500,
  fontSize: "var(--fs-display)",
  lineHeight: "var(--lh-display)",
  letterSpacing: "-0.02em",
  maxWidth: 960,
};

const H2: CSSProperties = {
  margin: 0,
  fontFamily: "var(--font-sans)",
  fontWeight: 500,
  fontSize: "var(--fs-h1)",
  lineHeight: "var(--lh-h1)",
  letterSpacing: "-0.01em",
};

const PARA: CSSProperties = {
  margin: 0,
  maxWidth: 720,
  fontSize: "var(--fs-body-lg)",
  lineHeight: "var(--lh-body-lg)",
  color: "var(--fg-secondary)",
};

const MONO_LINE: CSSProperties = {
  fontFamily: "var(--font-mono)",
  fontSize: "var(--fs-small)",
  lineHeight: "var(--lh-small)",
  color: "var(--fg-secondary)",
  margin: 0,
};

const DOWNLOAD_LINK: CSSProperties = {
  fontFamily: "var(--font-mono)",
  fontSize: "var(--fs-label)",
  letterSpacing: "0.04em",
  textTransform: "uppercase",
  color: "var(--ink)",
  textDecoration: "underline",
  textUnderlineOffset: "4px",
};

const ASSETS = [
  {
    title: "Wordmark — light surfaces",
    note: "Glyph + wordmark + teal terminator. Use on paper, white, or pale fills.",
    href: "/brand/humaniti-wordmark.svg",
    bg: "var(--paper)",
    fg: "var(--ink)",
    isWordmark: true,
  },
  {
    title: "Wordmark — dark surfaces",
    note: "Same lockup, paper-coloured strokes. Use on ink or saturated dark fills.",
    href: "/brand/humaniti-wordmark-on-dark.svg",
    bg: "var(--ink)",
    fg: "var(--paper)",
    isWordmark: true,
  },
  {
    title: "Glyph — light surfaces",
    note: "Standalone ⓗ. Square viewBox. Favicons, app icons, monograms.",
    href: "/brand/humaniti-glyph.svg",
    bg: "var(--paper)",
    fg: "var(--ink)",
    isWordmark: false,
  },
  {
    title: "Glyph — dark surfaces",
    note: "Same glyph, paper-coloured strokes. For dark backgrounds.",
    href: "/brand/humaniti-glyph-on-dark.svg",
    bg: "var(--ink)",
    fg: "var(--paper)",
    isWordmark: false,
  },
];

const SWATCHES = [
  {
    name: "Ink",
    hex: "#0F1719",
    rgb: "rgb(15 23 25)",
    lch: "lch(8 4 215)",
    use: "Primary type. Strokes. The default surface in dark mode.",
  },
  {
    name: "Teal",
    hex: "#1F4F5E",
    rgb: "rgb(31 79 94)",
    lch: "lch(31 16 226)",
    use: "Single accent. Teal terminator dot. Trust + civic infrastructure.",
  },
  {
    name: "Paper",
    hex: "#F5F6F4",
    rgb: "rgb(245 246 244)",
    lch: "lch(96 1 145)",
    use: "Default canvas. Cool, off-white, never pure #FFF.",
  },
  {
    name: "Signal Amber",
    hex: "#D97B2C",
    rgb: "rgb(217 123 44)",
    lch: "lch(61 56 51)",
    use: "Warnings + financial state. Used sparingly, never decoratively.",
  },
];

const BOILERPLATE_25 =
  "Humaniti is a verified-Human network for AI data labor. Builders publish work. Vetted Humans label it. Consensus verifies it. Humans get paid directly.";

const BOILERPLATE_75 =
  "Humaniti is a decentralized data network for AI. The work that trains frontier models — image bounding, text annotation, audio transcription, RLHF preference, expert review — is performed by verified Humans, not anonymous vendors. Builders publish tasks; the qualified pool labels them; N-Human consensus and per-skill Elo verify each label; Stewards adjudicate disputes; the signed dataset ships; the Humans get paid. The labor is auditable, the verification is transparent, and the people who did the work have their names on it.";

const BOILERPLATE_200 =
  "Humaniti is the decentralized data network behind verified AI. Today, almost every model that matters is trained on data labeled by people whose names you will never see, paid through vendors whose margins you will never know, and verified by methods you cannot audit. Humaniti inverts that. A Builder defines a task. A pool of verified Humans is selected by tier, skill, language, and region. Each item routes to N Humans independently. A consensus aggregator weights every label by per-skill Elo, surfaces disagreement, and routes contested items to T3 Stewards for final adjudication. Gold questions catch drift. Behavioral telemetry, generated-text classifiers, wallet and device clustering, and Steward audits make gaming uneconomic. The signed dataset ships. The Humans get paid directly, with no opaque vendor margin in between. Every label carries a receipt — the prompt, the labelers, the consensus history, the final adjudication, all queryable on an append-only audit log. The arc beyond launch is laid out in public — Network, then Record, then Economy, then Commons. Labor first, ownership next, governance last. Humaniti is the substrate. The labor is real, and the rules are written down.";

export const BrandPressPage = () => (
  <>
    <SEO
      title="Brand & Press"
      description="Brand assets, palette, typography, boilerplate copy, press kit. Use the Humaniti brand."
      path="/brand"
    />
    {/* 1. Lede */}
    <Section>
      <Container size="full">
        <Stack gap="2xl">
          <span style={SECTION_LABEL}>§ Brand and press</span>
          <h1 style={H1}>
            Use the Humaniti brand<span style={{ color: "var(--teal)" }}>.</span>
          </h1>
          <p style={PARA}>
            Use these freely for press, integration partner pages, and reference. Don't crop,
            recolour, or stretch the wordmark. Don't combine with another brand mark in a single
            lockup. Maintain a clear space equal to the height of the glyph on every side. If you
            need a one-line description or a longer write-up, copy from the boilerplate below — it
            stays current.
          </p>
        </Stack>
      </Container>
    </Section>

    {/* 2. Wordmark + glyph downloads */}
    <Section style={{ borderTop: "1px solid var(--slate-300)" }}>
      <Container size="full">
        <Stack gap="lg">
          <span style={SECTION_LABEL}>§ 01 — Marks</span>
          <h2 style={H2}>
            Wordmark and glyph<span style={{ color: "var(--teal)" }}>.</span>
          </h2>
          <Cluster gap="md" align="start">
            {ASSETS.map((a) => (
              <Card key={a.href} style={{ flex: 1, minWidth: 260 }}>
                <div
                  style={{
                    background: a.bg,
                    border: "1px solid var(--border-hairline)",
                    padding: "var(--space-6)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    minHeight: 120,
                    marginBottom: "var(--space-4)",
                  }}
                >
                  <img
                    src={a.href}
                    alt={a.title}
                    style={{
                      maxWidth: a.isWordmark ? 200 : 60,
                      maxHeight: a.isWordmark ? 56 : 60,
                      display: "block",
                    }}
                  />
                </div>
                <h3
                  style={{
                    margin: "0 0 var(--space-2)",
                    fontSize: "var(--fs-h3)",
                    fontFamily: "var(--font-sans)",
                    fontWeight: 500,
                  }}
                >
                  {a.title}
                </h3>
                <p
                  style={{
                    margin: "0 0 var(--space-4)",
                    color: "var(--fg-secondary)",
                    fontSize: "var(--fs-body)",
                    lineHeight: "var(--lh-body)",
                  }}
                >
                  {a.note}
                </p>
                <a download href={a.href} style={DOWNLOAD_LINK}>
                  Download SVG ↓
                </a>
              </Card>
            ))}
          </Cluster>
        </Stack>
      </Container>
    </Section>

    {/* 3. Color palette */}
    <Section style={{ borderTop: "1px solid var(--slate-300)" }}>
      <Container size="full">
        <Stack gap="lg">
          <span style={SECTION_LABEL}>§ 02 — Palette</span>
          <h2 style={H2}>
            Palette<span style={{ color: "var(--teal)" }}>.</span>
          </h2>
          <p style={PARA}>
            Four colours. Used at full strength. No shades or tints in marketing surfaces. Hex, RGB,
            and LCH values are listed below — all three are exact and copy-paste ready.
          </p>
          <Cluster gap="md" align="start">
            {SWATCHES.map((s) => (
              <Card key={s.hex} style={{ flex: 1, minWidth: 220 }}>
                <div
                  style={{
                    background: s.hex,
                    border:
                      s.hex === "#F5F6F4"
                        ? "1px solid var(--border-hairline)"
                        : "1px solid transparent",
                    height: 120,
                    marginBottom: "var(--space-4)",
                  }}
                  aria-label={`${s.name} swatch`}
                />
                <h3
                  style={{
                    margin: "0 0 var(--space-2)",
                    fontSize: "var(--fs-h3)",
                    fontFamily: "var(--font-sans)",
                    fontWeight: 500,
                  }}
                >
                  {s.name}
                </h3>
                <p style={MONO_LINE}>{s.hex}</p>
                <p style={MONO_LINE}>{s.rgb}</p>
                <p style={MONO_LINE}>{s.lch}</p>
                <p
                  style={{
                    margin: "var(--space-3) 0 0",
                    fontSize: "var(--fs-body)",
                    lineHeight: "var(--lh-body)",
                    color: "var(--fg-secondary)",
                  }}
                >
                  {s.use}
                </p>
              </Card>
            ))}
          </Cluster>
          <a download href="/brand/humaniti-color-palette.svg" style={DOWNLOAD_LINK}>
            Download palette reference (SVG) ↓
          </a>
        </Stack>
      </Container>
    </Section>

    {/* 4. Typography */}
    <Section style={{ borderTop: "1px solid var(--slate-300)" }}>
      <Container size="full">
        <Stack gap="lg">
          <span style={SECTION_LABEL}>§ 03 — Type</span>
          <h2 style={H2}>
            Type<span style={{ color: "var(--teal)" }}>.</span>
          </h2>
          <Cluster gap="md" align="start">
            <Card style={{ flex: 1, minWidth: 280 }}>
              <span style={SECTION_LABEL}>Sans</span>
              <h3
                style={{
                  margin: "var(--space-2) 0 var(--space-3)",
                  fontFamily: "var(--font-sans)",
                  fontWeight: 500,
                  fontSize: "var(--fs-h2)",
                  letterSpacing: "-0.01em",
                }}
              >
                Manrope.
              </h3>
              <p
                style={{
                  margin: "0 0 var(--space-3)",
                  fontSize: "var(--fs-body)",
                  lineHeight: "var(--lh-body)",
                  color: "var(--fg-secondary)",
                }}
              >
                Used for headings and body. Weights 400 and 500. Free under the SIL Open Font
                License.
              </p>
              <a
                href="https://fonts.google.com/specimen/Manrope"
                rel="noopener noreferrer"
                target="_blank"
                style={DOWNLOAD_LINK}
              >
                Get Manrope ↗
              </a>
            </Card>
            <Card style={{ flex: 1, minWidth: 280 }}>
              <span style={SECTION_LABEL}>Mono</span>
              <h3
                style={{
                  margin: "var(--space-2) 0 var(--space-3)",
                  fontFamily: "var(--font-mono)",
                  fontWeight: 500,
                  fontSize: "var(--fs-h2)",
                  letterSpacing: "-0.01em",
                }}
              >
                JetBrains Mono.
              </h3>
              <p
                style={{
                  margin: "0 0 var(--space-3)",
                  fontSize: "var(--fs-body)",
                  lineHeight: "var(--lh-body)",
                  color: "var(--fg-secondary)",
                }}
              >
                Used for systemic numerals, labels, status pills, and counters. Free under the SIL
                Open Font License.
              </p>
              <a
                href="https://fonts.google.com/specimen/JetBrains+Mono"
                rel="noopener noreferrer"
                target="_blank"
                style={DOWNLOAD_LINK}
              >
                Get JetBrains Mono ↗
              </a>
            </Card>
          </Cluster>
        </Stack>
      </Container>
    </Section>

    {/* 5. Photography */}
    <Section style={{ borderTop: "1px solid var(--slate-300)" }}>
      <Container size="full">
        <Stack gap="lg">
          <span style={SECTION_LABEL}>§ 04 — Photography</span>
          <h2 style={H2}>
            Photography<span style={{ color: "var(--teal)" }}>.</span>
          </h2>
          <p style={PARA}>
            Peer-level, declarative. No stock-photo tropes. No diverse-team-around-a-laptop. No
            hands-typing-on-keyboards. Real photographs of real Humans and real Builders, or no
            photographs at all. Black-and-white or natural light. Subjects look at the camera. Use
            ample negative space — the typography and the data are the visual subject.
          </p>
        </Stack>
      </Container>
    </Section>

    {/* 6. Boilerplate */}
    <Section style={{ borderTop: "1px solid var(--slate-300)" }}>
      <Container size="full">
        <Stack gap="lg">
          <span style={SECTION_LABEL}>§ 05 — Boilerplate</span>
          <h2 style={H2}>
            Boilerplate<span style={{ color: "var(--teal)" }}>.</span>
          </h2>
          <p style={PARA}>
            Press-ready descriptions at three lengths. Copy verbatim or trim — the substance is
            stable.
          </p>
          <Stack gap="md">
            <article>
              <span style={SECTION_LABEL}>25 words</span>
              <p
                style={{
                  margin: "var(--space-2) 0 0",
                  maxWidth: 720,
                  fontSize: "var(--fs-body-lg)",
                  lineHeight: "var(--lh-body-lg)",
                }}
              >
                {BOILERPLATE_25}
              </p>
            </article>
            <article>
              <span style={SECTION_LABEL}>75 words</span>
              <p
                style={{
                  margin: "var(--space-2) 0 0",
                  maxWidth: 720,
                  fontSize: "var(--fs-body-lg)",
                  lineHeight: "var(--lh-body-lg)",
                }}
              >
                {BOILERPLATE_75}
              </p>
            </article>
            <article>
              <span style={SECTION_LABEL}>200 words</span>
              <p
                style={{
                  margin: "var(--space-2) 0 0",
                  maxWidth: 720,
                  fontSize: "var(--fs-body-lg)",
                  lineHeight: "var(--lh-body-lg)",
                }}
              >
                {BOILERPLATE_200}
              </p>
            </article>
          </Stack>
        </Stack>
      </Container>
    </Section>

    {/* 7. Founder / team */}
    <Section style={{ borderTop: "1px solid var(--slate-300)" }}>
      <Container size="full">
        <Stack gap="lg">
          <span style={SECTION_LABEL}>§ 06 — Team</span>
          <h2 style={H2}>
            Team<span style={{ color: "var(--teal)" }}>.</span>
          </h2>
          <p style={PARA}>
            Bios pending review and consent for publication. Reach the team directly for press,
            interviews, and partnership inquiries.
          </p>
          <p style={MONO_LINE}>press@humaniti.io</p>
        </Stack>
      </Container>
    </Section>

    {/* 8. Press kit */}
    <Section style={{ borderTop: "1px solid var(--slate-300)" }}>
      <Container size="full">
        <Stack gap="lg">
          <span style={SECTION_LABEL}>§ 07 — Press kit</span>
          <h2 style={H2}>
            Press kit<span style={{ color: "var(--teal)" }}>.</span>
          </h2>
          <p style={PARA}>
            One archive with everything above — wordmark and glyph in light and dark, the palette
            reference, and a short README on permitted use.
          </p>
          <a download href="/brand/humaniti-press-kit.zip" style={DOWNLOAD_LINK}>
            Download press kit (.zip) ↓
          </a>
        </Stack>
      </Container>
    </Section>
  </>
);
